import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { motion } from 'framer-motion';
import { Download, CheckCircle2, Stethoscope, Calendar, ArrowRight, Phone, ShieldCheck, FileText, Clock } from 'lucide-react';
import { useScreenInit } from '../useScreenInit';
export function NewPatients() {
  useScreenInit();
  const insurers = ['Medicaid / Maryland Healthy Smiles', 'Delta Dental', 'Aetna', 'Cigna', 'MetLife', 'Guardian', 'Humana', 'UnitedHealthcare'];
  return <div className="min-h-screen bg-white selection:bg-teal-100 selection:text-teal-900" data-id="element-679">
      <Header data-id="element-680" />

      <main className="pt-40" data-id="element-681">
        {/* Welcoming Onboarding Hero */}
        <section className="pb-24 px-4" data-id="element-682">
          <div className="max-w-7xl mx-auto" data-id="element-683">
            <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-684">
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} data-id="element-685">
                <h1 className="text-4xl md:text-6xl font-bold text-[#225274] mb-6 leading-tight" data-id="element-686">
                  WELCOMING NEW PATIENTS TO COLESVILLE DENTAL CENTER.
                </h1>
                <p className="text-xl text-[#4F709C] leading-relaxed max-w-xl" data-id="element-687">
                  We make family dental care simple, highly transparent, and
                  entirely stress-free from day one.
                </p>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} className="grid sm:grid-cols-2 gap-4" data-id="element-688">
                <div className="bg-white border-2 border-slate-100 p-6 rounded-[12px] shadow-sm hover:shadow-md transition-shadow group" data-id="element-689">
                  <div className="w-10 h-10 bg-[#cef4d2]/50 rounded-full flex items-center justify-center mb-4 text-[#54c595] group-hover:bg-[#54c595] group-hover:text-white transition-colors" data-id="element-690">
                    <Download size={20} data-id="element-691" />
                  </div>
                  <h3 className="font-bold text-[#225274] mb-4 text-sm uppercase tracking-wider" data-id="element-692">
                    Medicaid New Patient Packet
                  </h3>
                  <button className="text-[#54c595] font-bold text-xs flex items-center gap-1 hover:underline" data-id="element-693">
                    Download PDF <ArrowRight size={14} data-id="element-694" />
                  </button>
                </div>
                <div className="bg-white border-2 border-slate-100 p-6 rounded-[12px] shadow-sm hover:shadow-md transition-shadow group" data-id="element-695">
                  <div className="w-10 h-10 bg-[#cef4d2]/50 rounded-full flex items-center justify-center mb-4 text-[#54c595] group-hover:bg-[#54c595] group-hover:text-white transition-colors" data-id="element-696">
                    <Download size={20} data-id="element-697" />
                  </div>
                  <h3 className="font-bold text-[#225274] mb-4 text-sm uppercase tracking-wider" data-id="element-698">
                    Private Insurance New Patient Forms
                  </h3>
                  <button className="text-[#54c595] font-bold text-xs flex items-center gap-1 hover:underline" data-id="element-699">
                    Download PDF <ArrowRight size={14} data-id="element-700" />
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Chronological Care Journey */}
        <section className="py-24 bg-slate-50" data-id="element-701">
          <div className="max-w-7xl mx-auto px-4" data-id="element-702">
            <div className="text-center mb-20" data-id="element-703">
              <h2 className="text-3xl font-bold text-[#225274] mb-4" data-id="element-704">
                YOUR JOURNEY TO A BETTER SMILE
              </h2>
              <div className="w-24 h-1 bg-[#54c595] mx-auto rounded-full" data-id="element-705" />
            </div>

            <div className="space-y-12" data-id="element-706">
              {/* Phase 1: Before */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="bg-white rounded-[12px] shadow-sm border border-slate-100 overflow-hidden" data-id="element-707">
                <div className="grid md:grid-cols-4" data-id="element-708">
                  <div className="bg-[#225274] p-8 text-white flex flex-col justify-center items-center text-center" data-id="element-709">
                    <div className="text-teal-400 mb-4" data-id="element-710">
                      <Clock size={40} data-id="element-711" />
                    </div>
                    <span className="text-xs font-bold tracking-[0.2em] mb-2 uppercase opacity-60" data-id="element-712">
                      Phase 1
                    </span>
                    <h3 className="text-xl font-bold" data-id="element-713">
                      BEFORE YOUR APPOINTMENT
                    </h3>
                  </div>
                  <div className="md:col-span-3 p-8 md:p-12" data-id="element-714">
                    <h4 className="font-bold text-[#225274] mb-6 text-lg" data-id="element-715">
                      Initial Preparation Checklist:
                    </h4>
                    <div className="grid sm:grid-cols-2 gap-y-4 gap-x-8 mb-8" data-id="element-716">
                      {['Previous dental X-rays', 'Current medication list', 'State or private insurance cards', 'Medical histories (BP, Diabetes, etc.)'].map((item, i) => <div key={i} className="flex items-center gap-3 text-slate-600" data-id="element-717">
                          <CheckCircle2 size={18} className="text-[#54c595] flex-shrink-0" data-id="element-718" />
                          <span className="text-sm font-medium" data-id="element-719">{item}</span>
                        </div>)}
                    </div>
                    <a href="#" className="inline-flex items-center gap-2 text-[#54c595] font-bold text-sm hover:gap-3 transition-all" data-id="element-720">
                      Pre-Fill Digital Intake Forms <ArrowRight size={16} data-id="element-721" />
                    </a>
                  </div>
                </div>
              </motion.div>

              {/* Phase 2: During */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="bg-white rounded-[12px] shadow-sm border border-slate-100 overflow-hidden" data-id="element-722">
                <div className="grid md:grid-cols-2" data-id="element-723">
                  <div className="h-64 md:h-auto overflow-hidden" data-id="element-724">
                    <img src="https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&q=80&w=800" alt="Clinical Exam" className="w-full h-full object-cover" data-id="element-725" />
                  </div>
                  <div className="p-8 md:p-12" data-id="element-726">
                    <div className="flex items-center gap-2 mb-4" data-id="element-727">
                      <span className="text-xs font-bold tracking-[0.2em] text-[#54c595] uppercase" data-id="element-728">
                        Phase 2
                      </span>
                      <div className="h-px flex-grow bg-slate-100" data-id="element-729" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#225274] mb-6" data-id="element-730">
                      DURING YOUR APPOINTMENT
                    </h3>
                    <div className="space-y-4 text-slate-600 text-sm leading-relaxed mb-8" data-id="element-731">
                      <p data-id="element-732">
                        Your visit begins with a comprehensive oral health
                        mapping and cancer screening. We use high-resolution
                        digital diagnostics to build your personalized smile
                        plan.
                      </p>
                      <p data-id="element-733">
                        Our team will walk you through every finding, ensuring
                        you have a complete understanding of your clinical
                        status before any treatment begins.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-734">
                      <span className="bg-[#cef4d2]/50 text-[#54c595] text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider border border-[#54c595]/20" data-id="element-735">
                        Medicaid Verification Handled Instantly
                      </span>
                      <span className="bg-slate-50 text-[#4bafbc] text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider border border-slate-100" data-id="element-736">
                        Flexible 3-Year Financing
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Phase 3: After */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="bg-white rounded-[12px] shadow-sm border border-slate-100 overflow-hidden" data-id="element-737">
                <div className="grid md:grid-cols-4" data-id="element-738">
                  <div className="bg-[#4F709C] p-8 text-white flex flex-col justify-center items-center text-center" data-id="element-739">
                    <div className="text-white mb-4" data-id="element-740">
                      <ShieldCheck size={40} data-id="element-741" />
                    </div>
                    <span className="text-xs font-bold tracking-[0.2em] mb-2 uppercase opacity-60" data-id="element-742">
                      Phase 3
                    </span>
                    <h3 className="text-xl font-bold" data-id="element-743">AFTER YOUR VISIT</h3>
                  </div>
                  <div className="md:col-span-3 p-8 md:p-12" data-id="element-744">
                    <h4 className="font-bold text-[#225274] mb-4 text-lg" data-id="element-745">
                      Long-Term Continuity & Care:
                    </h4>
                    <p className="text-slate-600 text-sm leading-relaxed mb-8 max-w-2xl" data-id="element-746">
                      Experience a transparent checkout flow where we provide
                      printed custom at-home care instructions. We'll help you
                      book your future 6-month routine appointments on-site or
                      through our secure online portal to ensure your smile
                      stays healthy.
                    </p>
                    <button className="bg-[#225274] text-white px-6 py-3 rounded-full font-bold text-sm hover:bg-[#1a3d58] transition-all uppercase tracking-wider" data-id="element-747">
                      Access Patient Portal
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Insurance Trust Grid */}
        <section className="py-24 bg-white" data-id="element-748">
          <div className="max-w-7xl mx-auto px-4" data-id="element-749">
            <h2 className="text-center text-xl font-bold text-[#225274] mb-16 tracking-[0.1em] uppercase" data-id="element-750">
              INSURANCES ACCEPTED & VERIFIED HERE
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16" data-id="element-751">
              {insurers.map((name, i) => <div key={i} className="h-20 border border-slate-100 rounded-lg flex items-center justify-center p-4 grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all cursor-default" data-id="element-752">
                  <span className="text-[10px] font-bold text-center text-slate-400 uppercase tracking-tighter" data-id="element-753">
                    {name}
                  </span>
                </div>)}
            </div>
            <div className="text-center" data-id="element-754">
              <a href="#" className="text-[#54c595] font-bold text-sm flex items-center justify-center gap-2 hover:underline" data-id="element-755">
                Don't See Your Plan? Check Your Custom Coverage Instantly{' '}
                <ArrowRight size={16} data-id="element-756" />
              </a>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-24 bg-slate-50" data-id="element-757">
          <div className="max-w-4xl mx-auto px-4" data-id="element-758">
            <div className="text-center mb-16" data-id="element-759">
              <h2 className="text-3xl font-bold text-[#225274] mb-4" data-id="element-760">
                FREQUENTLY ASKED QUESTIONS
              </h2>
              <div className="w-24 h-1 bg-[#54c595] mx-auto rounded-full" data-id="element-761" />
            </div>

            <div className="space-y-6" data-id="element-762">
              {[{
              q: 'How can I book an appointment?',
              a: <>
                      <p className="mb-2" data-id="element-763">3 different ways:</p>
                      <ul className="list-disc pl-5 space-y-1" data-id="element-764">
                        <li data-id="element-765">
                          By using our online booking system available on our
                          website.
                        </li>
                        <li data-id="element-766">
                          Visit us at 8737 Colesville Road #301, Silver Spring
                          MD, 20910 to make appointment, or
                        </li>
                        <li data-id="element-767">Call (301) 742 2231</li>
                      </ul>
                    </>
            }, {
              q: 'Do you offer emergency dental services?',
              a: 'Yes, we provide emergency dental services. If you have an urgent dental issue, please call our office immediately, and we will do our best to accommodate you as soon as possible.'
            }, {
              q: 'Do you accept Medical Assistance for BRACES?',
              a: 'We accept Medicaid for braces for patients under 21, pending insurance approval.'
            }, {
              q: 'What dental services do you offer?',
              a: 'At Colesville Dental, we offer a wide range of services including routine cleanings, fillings, crowns, bridges, root canals, and cosmetic dentistry such as teeth whitening and veneers.'
            }, {
              q: 'What services do you offer?',
              a: 'We are a full-service dental office providing comprehensive care, including braces (clear and traditional), extractions, dentures, and gold crowns, root canals, Dental Implants.'
            }, {
              q: 'Do you accept dental insurance?',
              a: 'Yes, we accept most major dental insurance plans including MEDICAID. Please contact our office to verify if we accept your specific insurance provider.'
            }, {
              q: 'Do you accept all payments?',
              a: 'We accept all forms of payment and offer convenient financing options through CareCredit and Sunbit.'
            }, {
              q: 'What technology do you use in your practice?',
              a: 'We use state-of-the-art technology including digital X-rays, laser soft tissue therapy, and same-day dental crowns to ensure the best care for our patients. We use state-of-the-art digital imaging and 3D printing technology to provide same-day crowns and dentures.'
            }, {
              q: 'How do I start my braces journey?',
              a: 'We offer a free braces consultation where we create a personalized treatment plan tailored to your needs and review the different payment options available.'
            }, {
              q: 'How much are your braces?',
              a: <>
                      <p className="mb-2" data-id="element-768">
                        The total cost of braces varies depending on several
                        factors, including the length of treatment and the type
                        of treatment needed. However, with a low down payment,
                        we offer affordable monthly payment plans starting at
                        $150 and up.
                      </p>
                      <p className="font-bold text-[#225274]" data-id="element-769">
                        WE OFFER FREE BRACES CONSULATION!!!!!
                      </p>
                    </>
            }].map((faq, i) => <div key={i} className="bg-white p-6 rounded-[12px] shadow-sm border border-slate-100" data-id="element-770">
                  <h3 className="text-lg font-bold text-[#225274] mb-3" data-id="element-771">
                    {faq.q}
                  </h3>
                  <div className="text-slate-600 text-sm leading-relaxed" data-id="element-772">
                    {faq.a}
                  </div>
                </div>)}
            </div>
          </div>
        </section>

        {/* Final Conversion Banner */}
        <section className="py-20 px-4 bg-white" data-id="element-773">
          <div className="max-w-5xl mx-auto bg-[#225274] rounded-[12px] p-10 md:p-16 text-center text-white shadow-2xl relative overflow-hidden" data-id="element-774">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32" data-id="element-775" />
            <div className="relative z-10" data-id="element-776">
              <h2 className="text-3xl md:text-4xl font-bold mb-10" data-id="element-777">
                Ready to Experience Painless,
                <br data-id="element-778" />
                Compassionate Dental Care?
              </h2>
              <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-779">
                <button onClick={() => window.location.href = '/book'} className="bg-gradient-to-r from-[#225274] via-[#4bafbc] to-[#54c595] hover:brightness-110 text-white px-10 py-4 rounded-full font-bold transition-all shadow-lg shadow-teal-900/20 uppercase tracking-widest" data-id="element-780">
                  BOOK ONLINE 24/7
                </button>
                <button className="border-2 border-white/30 hover:border-white text-white px-10 py-4 rounded-full font-bold transition-all uppercase tracking-widest" data-id="element-781">
                  Call Office: (410) 777-6984
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-782" />
    </div>;
}